import React, {
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';

import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Grid,
    TextField,
    Typography,
    CircularProgress,
    Chip,
    IconButton,
    DialogTitle,
    MenuItem,
    Select,
    Tooltip,
} from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import EditIcon from '@mui/icons-material/Edit';

import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';
import { toast } from 'react-hot-toast';

const { PRIVATE_ROUTE } = API_ROUTES;

// ---------------------------------------------------------
// Theme
// ---------------------------------------------------------

const TEAL = '#5CC3CC';
const TEAL_DARK = '#3BAEB8';
const TEAL_TEXT = '#4CAEB7';
const CARD_BACKGROUND = '#F5F8F9';
const BORDER_COLOR = '#D9E1E3';
const TEXT_PRIMARY = '#102A43';
const TEXT_SECONDARY = '#5F7485';

// ---------------------------------------------------------
// Status helpers
// ---------------------------------------------------------

const getStatusLabel = (status) => {
    switch (Number(status)) {
        case 1:
            return 'Approved';

        case 2:
            return 'Rejected';

        case 3:
            return 'Open';

        default:
            return 'Pending';
    }
};

const getStatusStyles = (status) => {
    switch (Number(status)) {
        case 1:
            return {
                backgroundColor: '#2E7D32',
                color: '#FFFFFF',
            };

        case 2:
            return {
                backgroundColor: '#D32F2F',
                color: '#FFFFFF',
            };

        case 3:
            return {
                backgroundColor: '#1976D2',
                color: '#FFFFFF',
            };

        default:
            return {
                backgroundColor: '#FFB000',
                color: '#111111',
            };
    }
};


// ---------------------------------------------------------
// Numeric helper
// ---------------------------------------------------------

const toNumber = (value, fallback = 0) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return fallback;
    }

    return Math.round(
        (number + Number.EPSILON) * 100
    ) / 100;
};

// ---------------------------------------------------------
// Get persisted manager-approved/allotted hours
//
// AUTHORITATIVE SOURCE:
// billing_line_items.labourCosts[].estimate_hour
//
// IMPORTANT:
// - Do NOT use task_hour.
// - Do NOT use top-level estimate_hour.
// - If labourCosts has no estimate_hour, return 0.
// ---------------------------------------------------------

const getDwrApprovedHours = (dwrItem) => {
    if (!dwrItem) {
        return 0;
    }

    const labourCosts =
        dwrItem?.billing_line_items?.labourCosts;

    if (!Array.isArray(labourCosts)) {
        return 0;
    }

    const totalEstimateHour =
        labourCosts.reduce(
            (total, labourItem) => {
                const estimateHour =
                    labourItem?.estimate_hour;

                if (
                    estimateHour === null ||
                    estimateHour === undefined ||
                    estimateHour === ''
                ) {
                    return total;
                }

                return toNumber(
                    total + Math.max(
                        toNumber(estimateHour),
                        0
                    )
                );
            },
            0
        );

    return toNumber(totalEstimateHour);
};

// ---------------------------------------------------------
// Information row
//
// IMPORTANT:
// This component MUST stay outside
// StaffDailyWorkReportView.
//
// Defining it inside the parent causes React to create
// a new component type on every render. When a TextField
// updates state, the component gets remounted and loses
// focus.
// ---------------------------------------------------------

const InfoRow = ({
    label,
    value,
    children,
    boldValue = false,
    editable = false,
    onEdit,
    onCancelEdit,
    isEditing = false,
    editDisabled = false,
}) => (
    <Box
        sx={{
            py: 1.05,
            borderBottom: `1px solid ${BORDER_COLOR}`,
            '&:last-child': { borderBottom: 'none' },
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
            }}
        >
            <Typography
                sx={{
                    fontSize: 12,
                    lineHeight: 1.2,
                    color: TEXT_SECONDARY,
                    mb: 0.35,
                }}
            >
                {label}
            </Typography>

            {editable && (
                <Tooltip
                    title={
                        isEditing
                            ? 'Cancel edit'
                            : 'Edit'
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            onClick={
                                isEditing
                                    ? onCancelEdit
                                    : onEdit
                            }
                            disabled={editDisabled}
                            sx={{
                                width: 28,
                                height: 28,
                                color: isEditing
                                    ? '#D32F2F'
                                    : TEAL_TEXT,

                                '&:hover': {
                                    backgroundColor:
                                        isEditing
                                            ? 'rgba(211,47,47,0.08)'
                                            : 'rgba(92,195,204,0.10)',
                                },
                            }}
                        >
                            {isEditing ? (
                                <ClearIcon
                                    sx={{
                                        fontSize: 19,
                                    }}
                                />
                            ) : (
                                <EditIcon
                                    sx={{
                                        fontSize: 17,
                                    }}
                                />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Box>

        {children || (
            <Typography
                sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    fontWeight: boldValue ? 700 : 500,
                    color: TEXT_PRIMARY,
                }}
            >
                {value !== undefined &&
                    value !== null &&
                    value !== ''
                    ? value
                    : '-'}
            </Typography>
        )}
    </Box>
);


// ---------------------------------------------------------
// Card header
//
// IMPORTANT:
// This component MUST stay outside
// StaffDailyWorkReportView.
// ---------------------------------------------------------

const CardHeader = ({
    icon,
    title,
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.2,
        }}
    >
        {React.cloneElement(icon, {
            sx: {
                fontSize: 20,
                color: TEAL_TEXT,
            },
        })}

        <Typography
            sx={{
                fontSize: 15,
                fontWeight: 700,
                color: TEXT_PRIMARY,
            }}
        >
            {title}
        </Typography>
    </Box>
);

// ---------------------------------------------------------
// Component
// ---------------------------------------------------------

const StaffDailyWorkReportView = ({
    open,
    onClose,
    dwrId,
    onUpdated,
}) => {
    const { authAxios } = useContext(FetchContext);

    const [loading, setLoading] =
        useState(false);

    const [actionLoading, setActionLoading] =
        useState(false);

    const [dwrData, setDwrData] =
        useState(null);

    const [editOptions, setEditOptions] = useState({
        projectManagers: [],
        employees: [],
        costItems: [],
    });

    const [linkedDwrOpen, setLinkedDwrOpen] =
        useState(false);

    const [linkedDwrs, setLinkedDwrs] =
        useState([]);

    // -------------------------------------------------------
    // HOURS
    // -------------------------------------------------------

    const [initialHours, setInitialHours] =
        useState(0);

    const [approvedHours, setApprovedHours] =
        useState('');

    const [totalEstimatedHours, setTotalEstimatedHours] =
        useState(0);

    const [totalEstimatedHoursEdit, setTotalEstimatedHoursEdit] =
        useState('0.00');

    const [estimatedHoursUpdateLoading, setEstimatedHoursUpdateLoading] =
        useState(false);

    const [totalSpentHours, setTotalSpentHours] =
        useState(0);

    const [remainingHours, setRemainingHours] =
        useState(0);

    const [remark, setRemark] =
        useState('');

    const [description, setDescription] =
        useState('');

    const [selectedCostItem, setSelectedCostItem] =
        useState(null);

    // ---------------------------------------------------------
    // ADMIN EDITABLE FIELDS
    // ---------------------------------------------------------

    const [editingFields, setEditingFields] = useState({});
    const [submitDate, setSubmitDate] = useState(null);
    const [taskDate, setTaskDate] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [dwrHoursEdit, setDwrHoursEdit] = useState('');


    const getCostItemKey = (item) => {
        if (!item) return '';

        const key =
            item?.uuid ??
            item?._id ??
            item?.id ??
            item?.cost_item_id ??
            item?.costItem ??
            item?.costitem ??
            item?.name ??
            '';

        return String(key).trim();
    };

    const getCostItemLabel = (item) => {
        if (!item) return '-';

        return (
            item?.costItem ||
            item?.costitem ||
            item?.name ||
            item?.description ||
            '-'
        );
    };

    const enableEdit = (field) => {
        if (field === 'cost_item' && dwrData) {
            const labourItem =
                dwrData?.billing_line_items
                    ?.labourCosts?.[0] || null;

            const currentUuid =
                labourItem?.uuid?.toString() || '';

            const currentName =
                (labourItem?.costitem || '')
                    .split(' - ')[0]
                    .trim();

            let selected = null;

            // First try the existing UUID
            if (currentUuid) {
                selected = costItemOptions.find(
                    (item) =>
                        getCostItemKey(item) === currentUuid
                ) || null;
            }

            // Then try the full existing cost-item text
            if (!selected && labourItem?.costitem) {
                selected = costItemOptions.find(
                    (item) => {
                        const label = getCostItemLabel(item);

                        return (
                            label === labourItem.costitem ||
                            label?.toString().trim() ===
                            labourItem.costitem
                                ?.toString()
                                .trim()
                        );
                    }
                ) || null;
            }

            // Finally try the part before " - "
            if (!selected && currentName) {
                selected = costItemOptions.find(
                    (item) =>
                        getCostItemLabel(item)
                            ?.toString()
                            .split(' - ')[0]
                            .trim() === currentName
                ) || null;
            }

            setSelectedCostItem(selected);
        }

        setEditingFields((previous) => ({
            ...previous,
            [field]: true,
        }));
    };

    const cancelEdit = (field) => {
        setEditingFields((previous) => {
            const next = { ...previous };
            delete next[field];
            return next;
        });

        if (!dwrData) {
            return;
        }

        switch (field) {

            case 'submit_date':
                setSubmitDate(
                    getDatePickerValue(
                        dwrData?.submit_date
                    )
                );
                break;

            case 'task_date':
                setTaskDate(
                    getDatePickerValue(
                        dwrData?.task_date
                    )
                );
                break;

            case 'cost_item': {
                const labourItem =
                    dwrData?.billing_line_items
                        ?.labourCosts?.[0] || null;

                const costItems =
                    dwrData?.taskdata
                        ?.billing_line_items
                        ?.labour_item
                        ?.labour_cost_items || [];

                const labourCostUuid =
                    labourItem?.uuid || null;

                const labourCostName =
                    (labourItem?.costitem || '')
                        .split(' - ')[0]
                        ?.trim();

                let originalCostItem = null;

                if (labourCostUuid) {
                    originalCostItem =
                        costItems.find(
                            (item) =>
                                item?.uuid ===
                                labourCostUuid
                        ) || null;
                }

                if (
                    !originalCostItem &&
                    labourCostName
                ) {
                    originalCostItem =
                        costItems.find(
                            (item) =>
                                item?.costItem ===
                                labourCostName ||
                                item?.costitem ===
                                labourCostName
                        ) || null;
                }

                setSelectedCostItem(
                    originalCostItem
                );

                break;
            }

            case 'employee':
                setSelectedEmployee(
                    dwrData?.user_id?.toString() || ''
                );
                break;

            case 'dwr_hours':
                setDwrHoursEdit(
                    toNumber(
                        dwrData?.task_hour
                    ).toFixed(2)
                );
                break;

            case 'remark':
                setRemark(
                    dwrData?.remark || ''
                );
                break;

            case 'description': {
                const labourItem =
                    dwrData?.billing_line_items
                        ?.labourCosts?.[0] || null;

                setDescription(
                    dwrData?.description ||
                    labourItem?.description ||
                    ''
                );

                break;
            }

            default:
                break;
        }
    };

    const getDatePickerValue = (value) => {
        if (!value) return null;
        if (typeof value === 'string') {
            const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) return dayjs(`${match[1]}-${match[2]}-${match[3]}`);
        }
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : null;
    };

    const formatDateForApi = (value) => {
        if (!value) return null;
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    };


    const employeeOptions = Array.isArray(
        editOptions?.employees
    )
        ? editOptions.employees
        : [];

    const costItemOptions = Array.isArray(
        editOptions?.costItems
    )
        ? editOptions.costItems
        : [];

    // ---------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------

    const formatDate = (value) => {
        if (!value) {
            return '-';
        }

        // Backend DWR dates are stored as UTC midnight.
        // Preserve calendar date.
        if (typeof value === 'string') {
            const match =
                value.match(
                    /^(\d{4})-(\d{2})-(\d{2})/
                );

            if (match) {
                const [
                    ,
                    year,
                    month,
                    day,
                ] = match;

                return `${month}/${day}/${year}`;
            }
        }

        const date =
            new Date(value);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return '-';
        }

        return `${String(
            date.getUTCMonth() + 1
        ).padStart(2, '0')}/${String(
            date.getUTCDate()
        ).padStart(2, '0')}/${date.getUTCFullYear()}`;
    };

    const getEmployeeName = () => {
        if (!dwrData) {
            return '-';
        }

        return (
            `${dwrData?.user_firstname || ''} ${dwrData?.user_lastname || ''
                }`.trim() || '-'
        );
    };

    const getTaskScope = () => {
        if (!dwrData) {
            return '-';
        }

        return (
            dwrData
                ?.scheduledata?.[0]
                ?.task_scope_id ||
            dwrData
                ?.taskdata
                ?.task_scope_id ||
            '-'
        );
    };

    const getProjectManagerName = () => {
        if (!dwrData) {
            return '-';
        }

        if (
            dwrData?.project_manager_detail
        ) {
            return (
                `${dwrData
                    ?.project_manager_detail
                    ?.first_name || ''
                    } ${dwrData
                        ?.project_manager_detail
                        ?.last_name || ''
                    }`.trim() || '-'
            );
        }

        if (
            Array.isArray(
                dwrData?.project_managers
            )
        ) {
            return (
                dwrData.project_managers
                    .map(
                        (manager) =>
                            `${manager?.first_name || ''
                                } ${manager?.last_name || ''
                                }`.trim()
                    )
                    .filter(Boolean)
                    .join(', ') || '-'
            );
        }

        return '-';
    };

    const getDwrEmployeeName = (
        linkedDwr
    ) => {
        if (
            !linkedDwr ||
            !dwrData
        ) {
            return '-';
        }

        const linkedUserId =
            linkedDwr?.user_id?.toString();

        const labourCosts =
            dwrData
                ?.billing_line_items
                ?.labourCosts;

        if (
            Array.isArray(labourCosts)
        ) {
            const labourEmployee =
                labourCosts.find(
                    (item) =>
                        item?.employee?.toString() ===
                        linkedUserId
                );

            if (
                labourEmployee?.employee_name
            ) {
                return labourEmployee.employee_name;
            }
        }

        if (
            dwrData?.user_id?.toString() ===
            linkedUserId
        ) {
            const currentEmployeeName =
                [
                    dwrData?.user_firstname,
                    dwrData?.user_lastname,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .trim();

            if (currentEmployeeName) {
                return currentEmployeeName;
            }
        }

        return '-';
    };

    // ---------------------------------------------------------
    // Get linked DWRs
    //
    // IMPORTANT:
    //
    // If allDwrForScheduleOrDraft exists but is EMPTY,
    // fall back to allDwrForTask.
    //
    // Empty array must NOT prevent fallback.
    // ---------------------------------------------------------

    const getLinkedDwrs = () => {
        if (!dwrData) {
            return [];
        }

        const currentScheduleId =
            dwrData?.schedule_id?.toString() ||
            null;

        const currentTodoDraftId =
            dwrData?.todo_draft_id?.toString() ||
            null;

        let allDwrs = [];

        // -------------------------------------------------------
        // Explicit relation collection
        //
        // Only use it when it contains records.
        // -------------------------------------------------------

        if (
            Array.isArray(
                dwrData?.allDwrForScheduleOrDraft
            ) &&
            dwrData
                .allDwrForScheduleOrDraft
                .length > 0
        ) {
            allDwrs =
                dwrData.allDwrForScheduleOrDraft;
        }

        // -------------------------------------------------------
        // FALLBACK
        // -------------------------------------------------------

        else if (
            Array.isArray(
                dwrData?.allDwrForTask
            )
        ) {
            allDwrs =
                dwrData.allDwrForTask.filter(
                    (item) => {
                        if (!item) {
                            return false;
                        }

                        const itemScheduleId =
                            item?.schedule_id?.toString() ||
                            null;

                        const itemTodoDraftId =
                            item?.todo_draft_id?.toString() ||
                            null;

                        // Schedule DWRs
                        if (currentScheduleId) {
                            return (
                                itemScheduleId ===
                                currentScheduleId
                            );
                        }

                        // TodoDraft DWRs
                        if (currentTodoDraftId) {
                            return (
                                itemTodoDraftId ===
                                currentTodoDraftId
                            );
                        }

                        return false;
                    }
                );
        }

        // -------------------------------------------------------
        // Only active records
        //
        // 0 = Pending
        // 1 = Approved
        //
        // Exclude:
        // 2 = Rejected
        // 3 = Open
        // deleted = true
        // -------------------------------------------------------

        return allDwrs.filter(
            (item) => {
                if (!item) {
                    return false;
                }

                if (
                    item?.is_deleted === true
                ) {
                    return false;
                }

                const status =
                    Number(item?.status);

                return (
                    status === 0 ||
                    status === 1
                );
            }
        );
    };

    // ---------------------------------------------------------
    // Determine whether this DWR belongs to Schedule
    // ---------------------------------------------------------

    const isScheduleDwr = (
        details
    ) => {
        if (!details) {
            return false;
        }

        return Boolean(
            details?.schedule_id ||
            details?.hoursource?.type ===
            'schedule' ||
            details?.scheduledata?.length ||
            details?.schedule
        );
    };

    // ---------------------------------------------------------
    // Determine whether this DWR belongs to TodoDraft
    // ---------------------------------------------------------

    const isTodoDraftDwr = (
        details
    ) => {
        if (!details) {
            return false;
        }

        return Boolean(
            details?.todo_draft_id ||
            details?.hoursource?.type ===
            'todo_draft' ||
            details?.tododraftdata?.length ||
            details?.todoDraft
        );
    };

    const hasParentEstimatedHoursRecord = (details) => {
        if (!details) {
            return false;
        }

        return Boolean(
            (isScheduleDwr(details) && details?.schedule_id) ||
            (isTodoDraftDwr(details) && details?.todo_draft_id)
        );
    };

    // ---------------------------------------------------------
    // Get Initial / Manager Estimated Hours
    // ---------------------------------------------------------

    const getInitialHours = (
        details
    ) => {
        if (!details) {
            return 0;
        }

        // =======================================================
        // SCHEDULE
        //
        // Schedule Estimated Hours / Assigned Crew
        // =======================================================

        if (
            isScheduleDwr(details)
        ) {
            const schedule =
                details
                    ?.scheduledata?.[0] ||
                details?.schedule ||
                null;

            const scheduleEstimatedHours =
                details
                    ?.hoursource
                    ?.estimated_hours ??
                schedule?.estimated_hours ??
                details?.estimated_hours ??
                0;

            const assignedMembers =
                Array.isArray(
                    schedule?.assigned_members
                )
                    ? schedule.assigned_members
                    : [];

            const crewCount =
                assignedMembers.length > 0
                    ? assignedMembers.length
                    : 1;

            const managerHours =
                toNumber(
                    scheduleEstimatedHours
                ) / crewCount;

            return Math.max(
                toNumber(managerHours),
                0
            );
        }

        // =======================================================
        // TODO DRAFT
        //
        // Initial manager hours = full TodoDraft estimate.
        // =======================================================

        if (
            isTodoDraftDwr(details)
        ) {
            const todoDraft =
                details
                    ?.todoDraftdata?.[0] ||
                details
                    ?.tododraftdata?.[0] ||
                details?.todoDraft ||
                null;

            const todoDraftEstimatedHours =
                details
                    ?.hoursource
                    ?.estimated_hours ??
                todoDraft?.estimated_hours ??
                details?.estimated_hours ??
                0;

            return Math.max(
                toNumber(
                    todoDraftEstimatedHours
                ),
                0
            );
        }

        // =======================================================
        // FALLBACK
        // =======================================================

        const fallbackValue =
            details
                ?.hoursource
                ?.estimated_hours ??
            details?.estimated_hours ??
            0;

        return Math.max(
            toNumber(fallbackValue),
            0
        );
    };

    // ---------------------------------------------------------
    // Load DWR
    // ---------------------------------------------------------

    const loadDwr = useCallback(async () => {
        if (!dwrId) {
            return;
        }

        try {
            setLoading(true);

            const { data } = await authAxios.get(
                `${PRIVATE_ROUTE.GET_DWR_BY_ID}/${dwrId}`
            );

            const dwrDetails = data?.data?.[0];

            if (!dwrDetails) {
                toast.error('DWR information not found.');
                return;
            }

            setDwrData(dwrDetails);
            setEditOptions(data?.editOptions || {});

            setSelectedEmployee(dwrDetails?.user_id?.toString() || '');
            setSubmitDate(getDatePickerValue(dwrDetails?.submit_date));
            setTaskDate(getDatePickerValue(dwrDetails?.task_date));
            setDwrHoursEdit(toNumber(dwrDetails?.task_hour).toFixed(2));
            setEditingFields({});

            // -------------------------------------------------------
            // Existing remark
            // -------------------------------------------------------

            setRemark(dwrDetails?.remark || '');

            // -------------------------------------------------------
            // Description
            // -------------------------------------------------------

            const labourItem =
                dwrDetails?.billing_line_items?.labourCosts?.[0] ||
                null;

            setDescription(
                dwrDetails?.description ||
                labourItem?.description ||
                ''
            );

            // -------------------------------------------------------
            // Find matching labour cost item from editOptions
            // -------------------------------------------------------

            const labourCost =
                dwrDetails?.billing_line_items
                    ?.labourCosts?.[0] || null;

            const currentCostItemText =
                labourCost?.costitem
                    ?.toString()
                    .trim() || '';

            const currentCostItemName =
                currentCostItemText
                    .split(' - ')[0]
                    .trim();

            const currentCostItemUuid =
                labourCost?.uuid?.toString() || '';

            const availableCostItems =
                Array.isArray(data?.editOptions?.costItems)
                    ? data.editOptions.costItems
                    : [];

            let selected = null;

            // 1. Match UUID / ID
            if (currentCostItemUuid) {
                selected =
                    availableCostItems.find(
                        (item) =>
                            getCostItemKey(item) ===
                            currentCostItemUuid
                    ) || null;
            }

            // 2. Match complete cost item text
            if (!selected && currentCostItemText) {
                selected =
                    availableCostItems.find(
                        (item) =>
                            getCostItemLabel(item)
                                ?.toString()
                                .trim() ===
                            currentCostItemText
                    ) || null;
            }

            // 3. Match name before " - "
            if (!selected && currentCostItemName) {
                selected =
                    availableCostItems.find(
                        (item) =>
                            getCostItemLabel(item)
                                ?.toString()
                                .split(' - ')[0]
                                .trim() ===
                            currentCostItemName
                    ) || null;
            }

            setSelectedCostItem(selected);

            // =======================================================
            // INITIAL HOURS
            // =======================================================

            const initialEstimate =
                getInitialHours(dwrDetails);

            setInitialHours(initialEstimate);

            // =======================================================
            // DWR HOUR
            // =======================================================

            const dwrHours = Math.max(
                toNumber(dwrDetails?.task_hour),
                0
            );

            // =======================================================
            // CURRENT DWR PERSISTED MANAGER ALLOCATION
            //
            // Read billing_line_items.labourCosts[].estimate_hour.
            // Do NOT use task_hour.
            // Do NOT require status === 1.
            // =======================================================

            const approvedEstimate =
                getDwrApprovedHours(dwrDetails);

            setApprovedHours(
                approvedEstimate.toFixed(2)
            );

            // =======================================================
            // GET LINKED DWRs
            //
            // IMPORTANT:
            // Use dwrDetails, NOT dwrData.
            //
            // React state update from setDwrData() is asynchronous.
            // =======================================================

            const currentScheduleId =
                dwrDetails?.schedule_id?.toString() || null;

            const currentTodoDraftId =
                dwrDetails?.todo_draft_id?.toString() || null;

            let allDwrsForScheduleOrDraft = [];

            // -------------------------------------------------------
            // First preference:
            // explicit schedule/todo draft relation
            // -------------------------------------------------------

            if (
                Array.isArray(
                    dwrDetails?.allDwrForScheduleOrDraft
                ) &&
                dwrDetails.allDwrForScheduleOrDraft.length > 0
            ) {
                allDwrsForScheduleOrDraft =
                    dwrDetails.allDwrForScheduleOrDraft;
            }

            // -------------------------------------------------------
            // Fallback:
            // all DWRs for task
            // -------------------------------------------------------

            else if (
                Array.isArray(dwrDetails?.allDwrForTask)
            ) {
                allDwrsForScheduleOrDraft =
                    dwrDetails.allDwrForTask.filter((item) => {
                        if (!item) {
                            return false;
                        }

                        if (item?.is_deleted === true) {
                            return false;
                        }

                        const itemScheduleId =
                            item?.schedule_id?.toString() || null;

                        const itemTodoDraftId =
                            item?.todo_draft_id?.toString() || null;

                        // Schedule DWR
                        if (currentScheduleId) {
                            return (
                                itemScheduleId ===
                                currentScheduleId
                            );
                        }

                        // TodoDraft DWR
                        if (currentTodoDraftId) {
                            return (
                                itemTodoDraftId ===
                                currentTodoDraftId
                            );
                        }

                        return false;
                    });
            }

            // -------------------------------------------------------
            // Only Pending / Approved
            // -------------------------------------------------------

            allDwrsForScheduleOrDraft =
                allDwrsForScheduleOrDraft.filter((item) => {
                    if (!item) {
                        return false;
                    }

                    if (item?.is_deleted === true) {
                        return false;
                    }

                    const status = Number(item?.status);

                    return (
                        status === 0 ||
                        status === 1
                    );
                });

            // -------------------------------------------------------
            // Keep linked DWR state available
            // -------------------------------------------------------

            setLinkedDwrs(
                allDwrsForScheduleOrDraft
            );

            // =======================================================
            // CURRENT DWR ID
            // =======================================================

            const currentId =
                dwrDetails?._id?.toString();

            // =======================================================
            // TOTAL EXISTING SPENT HOURS
            //
            // Only APPROVED linked DWRs.
            //
            // Uses persisted estimate_hour.
            //
            // Current DWR excluded.
            // =======================================================

            const totalExistingSpentHours =
                allDwrsForScheduleOrDraft.reduce(
                    (total, existingDwr) => {
                        if (
                            existingDwr?.is_deleted === true
                        ) {
                            return total;
                        }

                        const status =
                            Number(existingDwr?.status);

                        // Only approved DWRs count.
                        if (status !== 1) {
                            return total;
                        }

                        // Do not count current DWR here.
                        const existingId =
                            existingDwr?._id?.toString();

                        if (
                            existingId &&
                            currentId &&
                            existingId === currentId
                        ) {
                            return total;
                        }

                        const existingApprovedHours =
                            getDwrApprovedHours(
                                existingDwr
                            );

                        return toNumber(
                            total + existingApprovedHours
                        );
                    },
                    0
                );

            // =======================================================
            // CURRENT DWR APPROVED HOURS
            // =======================================================

            const currentApprovedHours =
                getDwrApprovedHours(
                    dwrDetails
                );

            // =======================================================
            // CURRENT DWR COUNTS TOWARD SPEND
            // ONLY WHEN APPROVED
            // =======================================================

            const currentDwrCountsAsSpent =
                Number(
                    dwrDetails?.status
                ) === 1;

            const totalUsedHours =
                toNumber(
                    totalExistingSpentHours +
                    (
                        currentDwrCountsAsSpent
                            ? currentApprovedHours
                            : 0
                    )
                );

            // =======================================================
            // TOTAL ESTIMATED HOURS
            //
            // Always full parent estimate.
            // =======================================================

            const calculatedTotalEstimatedHours =
                toNumber(
                    dwrDetails
                        ?.hoursource
                        ?.estimated_hours ??
                    dwrDetails
                        ?.scheduledata?.[0]
                        ?.estimated_hours ??
                    dwrDetails
                        ?.todoDraftdata?.[0]
                        ?.estimated_hours ??
                    dwrDetails
                        ?.tododraftdata?.[0]
                        ?.estimated_hours ??
                    dwrDetails?.estimated_hours ??
                    0
                );

            // =======================================================
            // REMAINING HOURS
            // =======================================================

            const remaining =
                toNumber(
                    Math.max(
                        calculatedTotalEstimatedHours -
                        totalUsedHours,
                        0
                    )
                );

            // =======================================================
            // SET HOURS
            // =======================================================

            setTotalEstimatedHours(
                calculatedTotalEstimatedHours
            );

            setTotalSpentHours(
                totalUsedHours
            );

            setRemainingHours(
                remaining
            );
        } catch (error) {
            console.error(
                'Failed to load DWR:',
                error
            );

            toast.error(
                'Failed to load DWR details.'
            );
        } finally {
            setLoading(false);
        }
    }, [
        authAxios,
        dwrId,
    ]);

    // ---------------------------------------------------------
    // Load when dialog opens
    // ---------------------------------------------------------

    useEffect(() => {
        if (
            open &&
            dwrId
        ) {
            loadDwr();
        }
    }, [
        open,
        dwrId,
        loadDwr,
    ]);

    // ---------------------------------------------------------
    // Reset when modal closes
    // ---------------------------------------------------------

    useEffect(() => {
        if (!open) {
            setDwrData(null);

            setInitialHours(0);

            setApprovedHours('');

            setTotalEstimatedHours(0);

            setTotalSpentHours(0);

            setRemainingHours(0);

            setRemark('');

            setDescription('');

            setSelectedCostItem(
                null
            );

            setLinkedDwrs([]);

            setLinkedDwrOpen(
                false
            );

            setEditingFields({});
            setSelectedEmployee('');
            setSubmitDate(null);
            setTaskDate(null);
            setDwrHoursEdit('');
        }
    }, [open]);

    const handleUpdateEstimatedHours = async () => {
        try {
            const numericHours = Number(totalEstimatedHoursEdit);

            if (
                totalEstimatedHoursEdit === '' ||
                !Number.isFinite(numericHours) ||
                numericHours < 0
            ) {
                toast.error('Please enter valid estimated hours.');
                return;
            }

            const isSchedule = isScheduleDwr(dwrData);

            const parentId = isSchedule
                ? dwrData?.schedule_id
                : dwrData?.todo_draft_id;

            if (!parentId) {
                toast.error('Schedule / To-Do Draft ID is missing.');
                return;
            }

            setEstimatedHoursUpdateLoading(true);

            const route = isSchedule
                ? PRIVATE_ROUTE.UPDATE_ESTIMATEDHOURS_SCHEDULE
                : PRIVATE_ROUTE.UPDATE_ESTIMATEDHOURS_TODO_DRAFT;

            const response = await authAxios.post(
                `${route}${parentId}`,
                {
                    estimated_hours: numericHours,
                }
            );

            if (
                response?.data?.statusCode === 200 ||
                response?.status === 200
            ) {
                toast.success(
                    response?.data?.message ||
                    'Estimated hours updated successfully.'
                );

                setEditingFields((previous) => {
                    const next = { ...previous };
                    delete next.total_estimated_hours;
                    return next;
                });

                // Reload DWR details to refresh parent estimate
                // and dependent hour calculations.
                await loadDwr();
            } else {
                toast.error(
                    response?.data?.message ||
                    'Failed to update estimated hours.'
                );
            }
        } catch (error) {
            console.error('Update estimated hours error:', error);

            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                'Failed to update estimated hours.'
            );
        } finally {
            setEstimatedHoursUpdateLoading(false);
        }
    };

    // ---------------------------------------------------------
    // APPROVE / REJECT
    // ---------------------------------------------------------

    const handleReject = async () => {
        try {
            if (!dwrData?._id) {
                toast.error('DWR ID is missing');
                return;
            }

            const estimateHour =
                dwrData?.billing_line_items?.labourCosts?.[0]?.estimate_hour ?? 0;

            const response = await authAxios.post(
                `${PRIVATE_ROUTE.EDIT_DWR_STATUS}${dwrData._id}`,
                {
                    status: 'reject',
                    estimateHour,
                    uuid:
                        dwrData?.billing_line_items?.labourCosts?.[0]?.uuid,
                    remark: dwrData?.remark ?? '',
                    description: dwrData?.description ?? '',
                }
            );

            if (
                response?.data?.statusCode === 200 ||
                response?.status === 200
            ) {
                toast.success(
                    response?.data?.message ||
                    'DWR rejected successfully'
                );

                if (typeof onUpdated === 'function') {
                    await onUpdated(response?.data?.data);
                }

                onClose();
            } else {
                toast.error(
                    response?.data?.message ||
                    'Failed to reject DWR'
                );
            }
        } catch (error) {
            console.error('Reject DWR error:', error);

            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                'Failed to reject DWR'
            );
        }
    };

    const handleAction = async () => {
        try {
            if (!dwrData?._id) {
                toast.error('DWR ID is missing');
                return;
            }

            // Validate hours
            const numericDwrHours = Number(dwrHoursEdit);
            const numericApprovedHours = Number(approvedHours);

            if (Number.isNaN(numericDwrHours)) {
                toast.error('Please enter valid DWR hours');
                return;
            }

            if (Number.isNaN(numericApprovedHours)) {
                toast.error('Please enter valid approved hours');
                return;
            }

            // Current labour item
            const currentLabourItem =
                dwrData?.billing_line_items?.labourCosts?.[0] || {};

            // Employee
            const employeeId =
                selectedEmployee ||
                currentLabourItem?.employee ||
                dwrData?.user_id ||
                null;

            // Cost item
            const selectedCostItemKey = getCostItemKey(selectedCostItem);

            const costItemName =
                getCostItemLabel(selectedCostItem) ||
                currentLabourItem?.costitem ||
                currentLabourItem?.costItem ||
                '';

            // Updated labour item
            const updatedLabourItem = {
                ...currentLabourItem,

                employee: employeeId,

                hours: numericDwrHours,

                estimate_hour: numericApprovedHours,

                costitem: costItemName,

                uuid:
                    selectedCostItem?.uuid ||
                    currentLabourItem?.uuid ||
                    selectedCostItemKey ||
                    null,

                description:
                    description ||
                    currentLabourItem?.description ||
                    '',
            };

            // Dates
            const formattedTaskDate = taskDate
                ? formatDateForApi(taskDate)
                : dwrData?.task_date || null;

            const formattedSubmitDate = submitDate
                ? formatDateForApi(submitDate)
                : dwrData?.submit_date || null;


            // Equipment
            const equipment = Array.isArray(
                dwrData?.billing_line_items?.equipment
            )
                ? dwrData.billing_line_items.equipment
                : [];

            // Final payload
            const payload = {
                task_id: dwrData?.task_id || null,

                task_name:
                    dwrData?.task_name ||
                    dwrData?.taskdata?.task_name ||
                    '',

                task_date: formattedTaskDate,

                submit_date: formattedSubmitDate,

                task_hour: numericDwrHours,

                user_id: employeeId,

                labourCosts: [updatedLabourItem],

                equipment,

                submit_status:
                    dwrData?.submit_status ??
                    dwrData?.status ??
                    0,

                remark:
                    remark ||
                    dwrData?.remark ||
                    '',

                description:
                    description ||
                    dwrData?.description ||
                    '',

                client_approved_DWR:
                    dwrData?.client_approved_DWR || '',

                representative_sign:
                    dwrData?.representative_sign || '',

                client_representative_sign:
                    dwrData?.client_representative_sign || '',

                client_representative:
                    dwrData?.client_representative || '',
                status: 1,

                project_managers_array:
                    Array.isArray(dwrData?.project_managers_array)
                        ? dwrData.project_managers_array
                        : [],
            };
            // Call new backend controller
            const response = await authAxios.post(
                `${PRIVATE_ROUTE.EDIT_DWR}${dwrData._id}`,
                payload
            );

            if (
                response?.data?.statusCode === 200 ||
                response?.status === 200
            ) {
                toast.success(
                    response?.data?.message ||
                    'DWR updated successfully'
                );

                // Refresh parent table/list
                if (typeof onUpdated === 'function') {
                    await onUpdated(response?.data?.data);
                }

                // Close modal
                onClose();
            } else {
                toast.error(
                    response?.data?.message ||
                    'Failed to update DWR'
                );
            }
        } catch (error) {
            console.error('Update DWR error:', error);

            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                'Failed to update DWR'
            );
        }
    };

    // ---------------------------------------------------------
    // Card styling
    // ---------------------------------------------------------

    const cardSx = {
        backgroundColor:
            CARD_BACKGROUND,

        border:
            `1px solid ${BORDER_COLOR}`,

        borderRadius: 1.5,

        boxShadow: 'none',

        p: 1.5,

        width: '100%',

        height: '100%',

        boxSizing: 'border-box',
    };

    // ---------------------------------------------------------
    // Dialog
    // ---------------------------------------------------------

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={Boolean(open)}
                onClose={
                    actionLoading
                        ? undefined
                        : onClose
                }
                fullWidth
                maxWidth="sm"
                scroll="paper"
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        boxShadow:
                            '0 10px 30px rgba(0, 0, 0, 0.20)',
                    },
                }}
            >
                {/* =====================================================
          LINKED DWR DIALOG
    ====================================================== */}

                <Dialog
                    open={linkedDwrOpen}
                    onClose={() =>
                        setLinkedDwrOpen(
                            false
                        )
                    }
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            borderRadius: 1.5,
                            overflow: 'hidden',
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            px: 2,
                            py: 1.5,
                            backgroundColor: TEAL,
                            color: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent:
                                'space-between',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 700,
                            }}
                        >
                            Linked DWR
                        </Typography>

                        <IconButton
                            onClick={() =>
                                setLinkedDwrOpen(
                                    false
                                )
                            }
                            sx={{
                                color: '#FFFFFF',
                                p: 0.5,
                            }}
                        >
                            <CloseIcon
                                sx={{
                                    fontSize: 21,
                                }}
                            />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            px: '16px !important',
                            pt: '12px !important',
                            pb: '16px !important',
                            backgroundColor:
                                '#FFFFFF',
                        }}
                    >
                        {linkedDwrs.length ===
                            0 ? (
                            <Box
                                sx={{
                                    py: 4,
                                    textAlign:
                                        'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        color:
                                            TEXT_SECONDARY,
                                    }}
                                >
                                    No linked DWRs
                                    found.
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection:
                                        'column',
                                    gap: 1.25,
                                }}
                            >
                                {linkedDwrs.map(
                                    (linkedDwr) => {
                                        const employeeName =
                                            [
                                                linkedDwr?.user_firstname,
                                                linkedDwr?.user_lastname,
                                            ]
                                                .filter(Boolean)
                                                .join(' ')
                                                .trim() ||
                                            linkedDwr
                                                ?.employee_name ||
                                            '-';

                                        // -------------------------------------------
                                        // Approved hours come from persisted manager
                                        // allocation.
                                        // -------------------------------------------

                                        const approvedHours =
                                            getDwrApprovedHours(linkedDwr);

                                        return (
                                            <Box
                                                key={
                                                    linkedDwr?._id
                                                }
                                                sx={{
                                                    p: 1.5,
                                                    border:
                                                        `1px solid ${BORDER_COLOR}`,
                                                    borderRadius: 1.5,
                                                    backgroundColor:
                                                        CARD_BACKGROUND,
                                                }}
                                            >
                                                {/* Top row */}

                                                <Box
                                                    sx={{
                                                        display:
                                                            'flex',
                                                        alignItems:
                                                            'center',
                                                        justifyContent:
                                                            'space-between',
                                                        gap: 1,
                                                        mb: 0.75,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            color:
                                                                TEXT_PRIMARY,
                                                        }}
                                                    >
                                                        DWR No # {
                                                            linkedDwr?.dwr_number ||
                                                            '-'
                                                        }
                                                    </Typography>

                                                    <Chip
                                                        label={getStatusLabel(
                                                            linkedDwr?.status
                                                        )}
                                                        size="small"
                                                        sx={{
                                                            height: 22,
                                                            fontSize: 10.5,
                                                            fontWeight: 600,
                                                            ...getStatusStyles(
                                                                linkedDwr?.status
                                                            ),
                                                        }}
                                                    />
                                                </Box>

                                                <Divider
                                                    sx={{
                                                        borderColor:
                                                            BORDER_COLOR,
                                                        mb: 0.75,
                                                    }}
                                                />

                                                {/* Details */}

                                                <Grid
                                                    container
                                                    spacing={1}
                                                >
                                                    <Grid
                                                        item
                                                        xs={6}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10.5,
                                                                color:
                                                                    TEXT_SECONDARY,
                                                            }}
                                                        >
                                                            Employee
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                fontSize: 12.5,
                                                                fontWeight: 600,
                                                                color:
                                                                    TEXT_PRIMARY,
                                                            }}
                                                        >
                                                            {employeeName}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        xs={6}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10.5,
                                                                color:
                                                                    TEXT_SECONDARY,
                                                            }}
                                                        >
                                                            Task Date
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                fontSize: 12.5,
                                                                fontWeight: 600,
                                                                color:
                                                                    TEXT_PRIMARY,
                                                            }}
                                                        >
                                                            {formatDate(
                                                                linkedDwr?.task_date
                                                            )}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        xs={6}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10.5,
                                                                color:
                                                                    TEXT_SECONDARY,
                                                            }}
                                                        >
                                                            DWR Hour
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                fontSize: 12.5,
                                                                fontWeight: 600,
                                                                color:
                                                                    TEXT_PRIMARY,
                                                            }}
                                                        >
                                                            {toNumber(
                                                                linkedDwr?.task_hour
                                                            ).toFixed(2)}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        xs={6}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10.5,
                                                                color:
                                                                    TEXT_SECONDARY,
                                                            }}
                                                        >
                                                            Approved Hours
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                fontSize: 12.5,
                                                                fontWeight: 600,
                                                                color:
                                                                    TEXT_PRIMARY,
                                                            }}
                                                        >
                                                            {approvedHours.toFixed(
                                                                2
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        );
                                    }
                                )}
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions
                        sx={{
                            px: 2,
                            py: 1.25,
                            borderTop:
                                `1px solid ${BORDER_COLOR}`,
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() =>
                                setLinkedDwrOpen(
                                    false
                                )
                            }
                            sx={{
                                height: 34,
                                px: 1.5,
                                borderRadius: 1,
                                textTransform:
                                    'none',
                                fontSize: 13,
                                fontWeight: 600,
                                color: TEAL_TEXT,
                                borderColor:
                                    TEAL_TEXT,

                                '&:hover': {
                                    borderColor:
                                        TEAL_DARK,
                                    backgroundColor:
                                        'rgba(92,195,204,0.06)',
                                },
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

    {/* =====================================================
          HEADER
    ====================================================== */}

                <DialogTitle
                    sx={{
                        p: 0,
                        backgroundColor: TEAL,
                        color: '#FFFFFF',
                    }}
                >
                    <Box
                        sx={{
                            minHeight: 62,
                            px: 2,
                            py: 1.25,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent:
                                'space-between',
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 18,
                                    lineHeight: 1.2,
                                    fontWeight: 700,
                                    color: '#FFFFFF',
                                }}
                            >
                                View DWR
                            </Typography>

                            {dwrData && (
                                <Typography
                                    sx={{
                                        mt: 0.35,
                                        fontSize: 12,
                                        lineHeight: 1.2,
                                        fontWeight: 600,
                                        color: '#FFFFFF',
                                    }}
                                >
                                    Job # {
                                        dwrData?.job_number ||
                                        '-'
                                    }{' '}
                                    | Task # {
                                        dwrData
                                            ?.taskdata
                                            ?.number_str ||
                                        '-'
                                    }
                                    
                                    <Typography
                                        component="span"
                                        sx={{
                                            mx: 0.75,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#FFFFFF',
                                        }}
                                    >
                                        |
                                    </Typography>

                                    <Chip
                                        label={
                                            dwrData?.taskdata?.is_completed === 1
                                                ? 'Completed'
                                                : 'Open'
                                        }
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            backgroundColor:
                                                dwrData?.taskdata?.is_completed === 1
                                                    ? '#C8E6C9'
                                                    : '#FFF3CD',
                                            color:
                                                dwrData?.taskdata?.is_completed === 1
                                                    ? '#1B5E20'
                                                    : '#856404',
                                            '& .MuiChip-label': {
                                                px: 1,
                                            },
                                        }}
                                    />
                                </Typography>
                            )}
                        </Box>

                        <IconButton
                            onClick={onClose}
                            disabled={
                                actionLoading
                            }
                            sx={{
                                color: '#FFFFFF',
                                p: 0.5,

                                '&:hover': {
                                    backgroundColor:
                                        'rgba(255,255,255,0.12)',
                                },
                            }}
                        >
                            <CloseIcon
                                sx={{
                                    fontSize: 23,
                                }}
                            />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {/* =====================================================
          DWR NUMBER / STATUS
      ====================================================== */}

                {dwrData && (
                    <Box
                        sx={{
                            px: 2,
                            py: 1.15,
                            backgroundColor:
                                '#FFFFFF',
                            borderBottom:
                                `1px solid ${BORDER_COLOR}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 500,
                                color:
                                    TEXT_SECONDARY,
                            }}
                        >
                            DWR No # {
                                dwrData?.dwr_number ||
                                '-'
                            }
                        </Typography>

                        <Chip
                            label={getStatusLabel(
                                dwrData?.status
                            )}
                            size="small"
                            sx={{
                                height: 24,
                                fontSize: 11,
                                fontWeight: 600,
                                ...getStatusStyles(
                                    dwrData?.status
                                ),
                            }}
                        />

                        {getLinkedDwrs().length >
                            0 && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        const related =
                                            getLinkedDwrs();

                                        setLinkedDwrs(
                                            related
                                        );

                                        setLinkedDwrOpen(
                                            true
                                        );
                                    }}
                                    sx={{
                                        ml: 'auto',
                                        minWidth:
                                            'auto',
                                        height: 28,
                                        px: 1.25,
                                        borderRadius: 1,
                                        textTransform:
                                            'none',
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        color:
                                            TEAL_TEXT,
                                        borderColor:
                                            TEAL_TEXT,
                                        whiteSpace:
                                            'nowrap',

                                        '&:hover': {
                                            borderColor:
                                                TEAL_DARK,
                                            backgroundColor:
                                                'rgba(92,195,204,0.06)',
                                        },
                                    }}
                                >
                                    Linked DWR
                                </Button>
                            )}
                    </Box>
                )}

                {/* =====================================================
          BODY
      ====================================================== */}

                <DialogContent
                    sx={{
                        p: 2,
                        backgroundColor:
                            '#FFFFFF',
                    }}
                >
                    {loading ? (
                        <Box
                            sx={{
                                minHeight: 400,
                                display: 'flex',
                                alignItems:
                                    'center',
                                justifyContent:
                                    'center',
                            }}
                        >
                            <CircularProgress
                                size={32}
                                sx={{
                                    color:
                                        TEAL_TEXT,
                                }}
                            />
                        </Box>
                    ) : !dwrData ? (
                        <Box
                            sx={{
                                minHeight: 300,
                                display: 'flex',
                                alignItems:
                                    'center',
                                justifyContent:
                                    'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    color:
                                        TEXT_SECONDARY,
                                    fontSize: 14,
                                }}
                            >
                                No DWR information
                                found.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid
                            container
                            spacing={1.5}
                            sx={{
                                alignItems:
                                    'stretch',
                            }}
                        >
                            {/* =================================================
                LEFT CARD - DWR DETAILS
            ================================================== */}

                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    display: 'flex',
                                    minWidth: 0,
                                }}
                            >
                                <Card sx={cardSx}>
                                    <CardHeader
                                        icon={
                                            <PersonOutlineIcon />
                                        }
                                        title="DWR Details"
                                    />

                                    <Divider
                                        sx={{
                                            borderColor:
                                                BORDER_COLOR,
                                            mb: 0.5,
                                        }}
                                    />

                                    <InfoRow
                                        label="Task Scope"
                                        value={getTaskScope()}
                                    />

                                    <InfoRow
                                        label="Project Manager"
                                        value={getProjectManagerName()}
                                    />

                                    <InfoRow
                                        label="Submit Date"
                                        value={formatDate(dwrData?.submit_date)}
                                    //     editable
                                    //     isEditing={editingFields.submit_date}
                                    //     onEdit={() =>
                                    //         enableEdit('submit_date')
                                    //     }
                                    //     onCancelEdit={() =>
                                    //         cancelEdit('submit_date')
                                    //     }
                                    //     editDisabled={actionLoading}
                                    // >
                                    //     {editingFields.submit_date ? (
                                    //         <DatePicker
                                    //             value={submitDate}
                                    //             onChange={setSubmitDate}
                                    //             format="MM/DD/YYYY"
                                    //             renderInput={(params) => (
                                    //                 <TextField
                                    //                     {...params}
                                    //                     fullWidth
                                    //                     size="small"
                                    //                     sx={{
                                    //                         '& .MuiOutlinedInput-root': {
                                    //                             backgroundColor: '#FFFFFF',

                                    //                             '& fieldset': {
                                    //                                 borderColor: TEAL_TEXT,
                                    //                             },

                                    //                             '&:hover fieldset': {
                                    //                                 borderColor: TEAL_DARK,
                                    //                             },

                                    //                             '&.Mui-focused fieldset': {
                                    //                                 borderColor: TEAL_TEXT,
                                    //                             },
                                    //                         },
                                    //                     }}
                                    //                 />
                                    //             )}
                                    //         />
                                    //     ) : (
                                    //         <Typography sx={{ fontSize: 14, fontWeight: 500, color: TEXT_PRIMARY }}>
                                    //             {formatDate(dwrData?.submit_date)}
                                    //         </Typography>
                                    //     )}
                                    // </InfoRow>
                                    />

                                    <InfoRow
                                        label="Task Date"
                                        value={formatDate(dwrData?.task_date)}
                                        editable
                                        isEditing={editingFields.task_date}
                                        onEdit={() =>
                                            enableEdit('task_date')
                                        }
                                        onCancelEdit={() =>
                                            cancelEdit('task_date')
                                        }
                                        editDisabled={actionLoading}
                                    >
                                        {editingFields.task_date ? (
                                            <DatePicker
                                                value={taskDate}
                                                onChange={setTaskDate}
                                                format="MM/DD/YYYY"
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: '#FFFFFF',

                                                                '& fieldset': {
                                                                    borderColor: TEAL_TEXT,
                                                                },

                                                                '&:hover fieldset': {
                                                                    borderColor: TEAL_DARK,
                                                                },

                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: TEAL_TEXT,
                                                                },
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: TEXT_PRIMARY }}>
                                                {formatDate(dwrData?.task_date)}
                                            </Typography>
                                        )}
                                    </InfoRow>

                                    <InfoRow
                                        label="Cost Item"
                                        value={
                                            dwrData?.billing_line_items
                                                ?.labourCosts?.[0]?.costitem
                                        }
                                        editable
                                        isEditing={editingFields.cost_item}
                                        onEdit={() =>
                                            enableEdit('cost_item')
                                        }
                                        onCancelEdit={() =>
                                            cancelEdit('cost_item')
                                        }
                                        editDisabled={actionLoading}
                                    >
                                        {editingFields.cost_item ? (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={getCostItemKey(selectedCostItem)}
                                                displayEmpty
                                                onChange={(event) => {
                                                    const selectedKey =
                                                        String(event.target.value || '');

                                                    const selected =
                                                        costItemOptions.find(
                                                            (item) =>
                                                                getCostItemKey(item) ===
                                                                selectedKey
                                                        ) || null;

                                                    setSelectedCostItem(selected);
                                                }}
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return (
                                                            <span
                                                                style={{
                                                                    color: TEXT_SECONDARY,
                                                                }}
                                                            >
                                                                Select Cost Item
                                                            </span>
                                                        );
                                                    }

                                                    const selectedItem =
                                                        costItemOptions.find(
                                                            (item) =>
                                                                getCostItemKey(item) ===
                                                                String(selected)
                                                        );

                                                    return getCostItemLabel(selectedItem);
                                                }}
                                                sx={{
                                                    backgroundColor: '#FFFFFF',

                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TEAL_TEXT,
                                                    },

                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TEAL_DARK,
                                                    },

                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TEAL_TEXT,
                                                    },
                                                }}
                                            >
                                                {costItemOptions.map((item) => {
                                                    const key = getCostItemKey(item);

                                                    return (
                                                        <MenuItem
                                                            key={key}
                                                            value={key}
                                                        >
                                                            {getCostItemLabel(item)}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        ) : (
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: TEXT_PRIMARY }}>
                                                {dwrData?.billing_line_items?.labourCosts?.[0]?.costitem || '-'}
                                            </Typography>
                                        )}
                                    </InfoRow>

                                    <InfoRow
                                        label="Employee Name"
                                        value={getEmployeeName()}
                                        editable
                                        isEditing={editingFields.employee}
                                        onEdit={() =>
                                            enableEdit('employee')
                                        }
                                        onCancelEdit={() =>
                                            cancelEdit('employee')
                                        }
                                        editDisabled={actionLoading}
                                    >
                                        {editingFields.employee ? (
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={selectedEmployee}
                                                onChange={(event) => setSelectedEmployee(event.target.value)}
                                                sx={{
                                                    backgroundColor: '#FFFFFF',

                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TEAL_TEXT,
                                                    },

                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TEAL_DARK,
                                                    },

                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: TEAL_TEXT,
                                                    },
                                                }}
                                            >
                                                {employeeOptions.map((employee) => {
                                                    const id =
                                                        employee?._id ||
                                                        employee?.id ||
                                                        employee?.user_id ||
                                                        employee?.employee;

                                                    const name =
                                                        employee?.employee_name ||
                                                        employee?.name ||
                                                        `${employee?.first_name || employee?.firstname || ''} ${employee?.last_name || employee?.lastname || ''
                                                            }`.trim();

                                                    return (
                                                        <MenuItem
                                                            key={id}
                                                            value={id?.toString() || ''}
                                                        >
                                                            {name || '-'}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        ) : (
                                            <Typography sx={{ fontSize: 14, fontWeight: 500, color: TEXT_PRIMARY }}>
                                                {getEmployeeName()}
                                            </Typography>
                                        )}
                                    </InfoRow>
                                </Card>
                            </Grid>

                            {/* =================================================
                RIGHT CARD - HOURS
            ================================================== */}

                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    display: 'flex',
                                    minWidth: 0,
                                }}
                            >
                                <Card sx={cardSx}>
                                    <CardHeader
                                        icon={
                                            <AccessTimeIcon />
                                        }
                                        title="Hours"
                                    />

                                    <Divider
                                        sx={{
                                            borderColor:
                                                BORDER_COLOR,
                                            mb: 0.5,
                                        }}
                                    />

                                    {/* =============================================
                    MANAGER ESTIMATED HOURS
                ============================================== */}

                                    <Box
                                        sx={{
                                            mt: 0.5,
                                            mb: 1.25,
                                            p: 1,
                                            backgroundColor:
                                                '#FFFFFF',
                                            border:
                                                `1px solid ${BORDER_COLOR}`,
                                            borderRadius: 1,
                                            height: 115,
                                            display: 'flex',
                                            flexDirection:
                                                'column',
                                            boxSizing:
                                                'border-box',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color:
                                                    TEXT_SECONDARY,
                                                pb: 0.75,
                                                borderBottom:
                                                    `1px solid ${BORDER_COLOR}`,
                                            }}
                                        >
                                            Manager Estimated
                                            Hours
                                        </Typography>

                                        <Box
                                            sx={{
                                                flex: 1,
                                                display:
                                                    'flex',
                                                justifyContent:
                                                    'center',
                                                alignItems:
                                                    'center',
                                                gap: 2,
                                            }}
                                        >
                                            {/* Initial */}

                                            <TextField
                                                label="Initial"
                                                value={Number(
                                                    initialHours
                                                ).toFixed(2)}
                                                disabled
                                                InputProps={{
                                                    readOnly:
                                                        true,
                                                }}
                                                size="small"
                                                sx={{
                                                    width: 90,

                                                    '& .MuiOutlinedInput-root':
                                                    {
                                                        height: 38,
                                                        borderRadius:
                                                            1.25,
                                                        backgroundColor:
                                                            '#f7f8fa',
                                                    },

                                                    '& .MuiOutlinedInput-input':
                                                    {
                                                        padding:
                                                            '6px',
                                                        textAlign:
                                                            'center',
                                                        fontSize:
                                                            '0.9rem',
                                                        fontWeight: 600,
                                                    },

                                                    '& .MuiInputLabel-root':
                                                    {
                                                        fontSize:
                                                            '0.8rem',
                                                    },

                                                    '& .MuiInputLabel-shrink':
                                                    {
                                                        backgroundColor:
                                                            '#fff',
                                                        padding:
                                                            '0 4px',
                                                    },
                                                }}
                                            />

                                            {/* Approved */}

                                            <TextField
                                                label="Approved"
                                                value={
                                                    approvedHours ===
                                                        ''
                                                        ? ''
                                                        : approvedHours
                                                }
                                                onChange={(
                                                    e
                                                ) => {
                                                    const value =
                                                        e.target
                                                            .value;

                                                    if (
                                                        value ===
                                                        ''
                                                    ) {
                                                        setApprovedHours(
                                                            ''
                                                        );

                                                        return;
                                                    }

                                                    if (
                                                        /^\d*\.?\d{0,2}$/.test(
                                                            value
                                                        )
                                                    ) {
                                                        setApprovedHours(
                                                            value
                                                        );
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (
                                                        approvedHours !==
                                                        ''
                                                    ) {
                                                        const numericValue =
                                                            Number(
                                                                approvedHours
                                                            );

                                                        if (
                                                            Number.isFinite(
                                                                numericValue
                                                            )
                                                        ) {
                                                            setApprovedHours(
                                                                numericValue.toFixed(
                                                                    2
                                                                )
                                                            );
                                                        }
                                                    }
                                                }}
                                                disabled={actionLoading}
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.01,
                                                }}
                                                size="small"
                                                sx={{
                                                    width: 90,

                                                    '& .MuiOutlinedInput-root':
                                                    {
                                                        height: 38,
                                                        borderRadius:
                                                            1.25,
                                                    },

                                                    '& .MuiOutlinedInput-input':
                                                    {
                                                        padding:
                                                            '6px',
                                                        textAlign:
                                                            'center',
                                                        fontSize:
                                                            '0.9rem',
                                                        fontWeight: 600,
                                                    },

                                                    '& .MuiInputLabel-root':
                                                    {
                                                        fontSize:
                                                            '0.8rem',
                                                    },

                                                    '& .MuiInputLabel-shrink':
                                                    {
                                                        backgroundColor:
                                                            '#fff',
                                                        padding:
                                                            '0 4px',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Total Estimated Hours */}

                                    {/* Total Estimated Hours */}

                                    <InfoRow
                                        label="Total Estimated Hours"
                                        value={Number(totalEstimatedHours).toFixed(2)}
                                        editable={hasParentEstimatedHoursRecord(dwrData)}
                                        isEditing={editingFields.total_estimated_hours}
                                        onEdit={() => {
                                            setTotalEstimatedHoursEdit(
                                                Number(totalEstimatedHours).toFixed(2)
                                            );

                                            enableEdit('total_estimated_hours');
                                        }}
                                        onCancelEdit={() => {
                                            setTotalEstimatedHoursEdit(
                                                Number(totalEstimatedHours).toFixed(2)
                                            );

                                            cancelEdit('total_estimated_hours');
                                        }}
                                        editDisabled={
                                            actionLoading ||
                                            estimatedHoursUpdateLoading
                                        }
                                    >
                                        {editingFields.total_estimated_hours ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <TextField
                                                    size="small"
                                                    value={totalEstimatedHoursEdit}
                                                    onChange={(event) => {
                                                        const value = event.target.value;

                                                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                                                            setTotalEstimatedHoursEdit(value);
                                                        }
                                                    }}
                                                    sx={{
                                                        flex: 1,
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: '#FFFFFF',
                                                        },
                                                    }}
                                                />

                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={handleUpdateEstimatedHours}
                                                    disabled={estimatedHoursUpdateLoading}
                                                    sx={{
                                                        minWidth: 75,
                                                        height: 40,
                                                        backgroundColor: TEAL_DARK,
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            backgroundColor: TEAL_TEXT,
                                                        },
                                                    }}
                                                >
                                                    {estimatedHoursUpdateLoading ? (
                                                        <CircularProgress
                                                            size={18}
                                                            sx={{ color: '#FFFFFF' }}
                                                        />
                                                    ) : (
                                                        'Update'
                                                    )}
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: TEXT_PRIMARY,
                                                }}
                                            >
                                                {Number(totalEstimatedHours).toFixed(2)}
                                            </Typography>
                                        )}
                                    </InfoRow>

                                    {/* DWR Hour */}

                                    <InfoRow
                                        label="DWR Hour"
                                        value={Number(
                                            dwrHoursEdit ||
                                            dwrData?.task_hour ||
                                            0
                                        ).toFixed(2)}
                                        boldValue
                                        editable
                                        isEditing={editingFields.dwr_hours}
                                        onEdit={() =>
                                            enableEdit('dwr_hours')
                                        }
                                        onCancelEdit={() =>
                                            cancelEdit('dwr_hours')
                                        }
                                        editDisabled={actionLoading}
                                    >
                                        {editingFields.dwr_hours ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={dwrHoursEdit}
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    if (/^\d*\.?\d{0,2}$/.test(value)) setDwrHoursEdit(value);
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#FFFFFF',

                                                        '& fieldset': {
                                                            borderColor: TEAL_TEXT,
                                                        },

                                                        '&:hover fieldset': {
                                                            borderColor: TEAL_DARK,
                                                        },

                                                        '&.Mui-focused fieldset': {
                                                            borderColor: TEAL_TEXT,
                                                        },
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>
                                                {Number(dwrHoursEdit || dwrData?.task_hour || 0).toFixed(2)}
                                            </Typography>
                                        )}
                                    </InfoRow>

                                    {/* Total Spent Hours */}

                                    <InfoRow
                                        label="Total Spent Hours"
                                        value={Number(
                                            totalSpentHours
                                        ).toFixed(2)}
                                    />

                                    {/* Remaining Hours */}

                                    <InfoRow
                                        label="Remaining Hours"
                                        value={Number(
                                            remainingHours
                                        ).toFixed(2)}
                                    />
                                </Card>
                            </Grid>

                            {/* =================================================
                BOTTOM CARD - ADDITIONAL DETAILS
            ================================================== */}

                            <Grid
                                item
                                xs={12}
                            >
                                <Card sx={cardSx}>
                                    <CardHeader
                                        icon={
                                            <BusinessCenterOutlinedIcon />
                                        }
                                        title="Additional Details"
                                    />

                                    <Divider
                                        sx={{
                                            borderColor:
                                                BORDER_COLOR,
                                            mb: 0.5,
                                        }}
                                    />

                                    {/* Remarks */}

                                    <InfoRow
                                        label="Remarks"
                                        editable
                                        isEditing={editingFields.remark}
                                        onEdit={() =>
                                            enableEdit('remark')
                                        }
                                        onCancelEdit={() =>
                                            cancelEdit('remark')
                                        }
                                        editDisabled={actionLoading}
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            minRows={2}
                                            value={remark}
                                            onChange={(
                                                event
                                            ) =>
                                                setRemark(
                                                    event.target
                                                        .value
                                                )
                                            }
                                            disabled={actionLoading || !editingFields.remark}
                                            placeholder="Enter remarks"
                                            sx={{
                                                mt: 0.25,

                                                '& .MuiOutlinedInput-root':
                                                {
                                                    backgroundColor:
                                                        '#FFFFFF',

                                                    '& fieldset': {
                                                        borderColor:
                                                            BORDER_COLOR,
                                                    },

                                                    '&:hover fieldset':
                                                    {
                                                        borderColor:
                                                            TEAL_TEXT,
                                                    },

                                                    '&.Mui-focused fieldset':
                                                    {
                                                        borderColor:
                                                            TEAL_TEXT,
                                                    },
                                                },

                                                '& .MuiInputBase-input':
                                                {
                                                    fontSize: 14,
                                                    color:
                                                        TEXT_PRIMARY,
                                                },
                                            }}
                                        />
                                    </InfoRow>

                                    {/* Description */}

                                    <InfoRow
                                        label="Description"
                                        value={description}
                                        editable
                                        isEditing={editingFields.description}
                                        onEdit={() =>
                                            enableEdit('description')
                                        }
                                        onCancelEdit={() =>
                                            cancelEdit('description')
                                        }
                                        editDisabled={actionLoading}
                                    >
                                        {editingFields.description ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                multiline
                                                minRows={2}
                                                value={description}
                                                onChange={(event) => setDescription(event.target.value)}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#FFFFFF',

                                                        '& fieldset': {
                                                            borderColor: TEAL_TEXT,
                                                        },

                                                        '&:hover fieldset': {
                                                            borderColor: TEAL_DARK,
                                                        },

                                                        '&.Mui-focused fieldset': {
                                                            borderColor: TEAL_TEXT,
                                                        },
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <Typography sx={{ fontSize: 14, lineHeight: 1.4, fontWeight: 500, color: TEXT_PRIMARY, whiteSpace: 'pre-wrap' }}>
                                                {description || '-'}
                                            </Typography>
                                        )}
                                    </InfoRow>

                                    {/* Client Address */}

                                    {dwrData
                                        ?.taskdata
                                        ?.client_address && (
                                            <InfoRow
                                                label="Client Address"
                                                value={
                                                    dwrData
                                                        .taskdata
                                                        .client_address
                                                }
                                            />
                                        )}
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>

                {/* =====================================================
          FOOTER
      ====================================================== */}

                <Divider
                    sx={{
                        borderColor:
                            BORDER_COLOR,
                    }}
                />

                <DialogActions
                    sx={{
                        px: 2,
                        py: 1.25,
                        backgroundColor:
                            '#FFFFFF',
                        justifyContent:
                            'flex-end',
                        gap: 1,
                    }}
                >
                    {/* Close */}

                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={
                            actionLoading
                        }
                        startIcon={
                            <CloseIcon />
                        }
                        sx={{
                            minWidth: 72,
                            height: 36,
                            px: 1.5,
                            borderRadius: 1,
                            textTransform:
                                'none',
                            fontSize: 13,
                            fontWeight: 600,
                            color: TEAL_TEXT,
                            borderColor:
                                TEAL_TEXT,

                            '&:hover': {
                                borderColor:
                                    TEAL_DARK,
                                backgroundColor:
                                    'rgba(92,195,204,0.06)',
                            },
                        }}
                    >
                        Close
                    </Button>
                    {/* Rejected */}

                    <Button
                        variant="outlined"
                        onClick={handleReject}
                        disabled={
                            actionLoading ||
                            loading ||
                            !dwrData
                        }
                        sx={{
                            minWidth: 90,
                            height: 36,
                            px: 1.5,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#D32F2F',
                            borderColor: '#D32F2F',

                            '&:hover': {
                                borderColor: '#B71C1C',
                                backgroundColor:
                                    'rgba(211,47,47,0.06)',
                            },
                        }}
                    >
                        Reject
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={handleAction}
                        disabled={actionLoading || loading || !dwrData}
                        sx={{
                            minWidth: 95,
                            height: 36,
                            px: 1.75,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontSize: 13,
                            fontWeight: 600,
                            backgroundColor: TEAL,
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: TEAL_DARK, boxShadow: 'none' },
                        }}
                    >
                        {actionLoading ? 'Processing...' : 'Save & Approve'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default StaffDailyWorkReportView;